import {
  naturalCollator,
  stackValueLabel,
} from "@/composables/filterableChartUtils";
import type { SelectableFilterColumn } from "@/dashboard/inventory_management/common/portfolioTableColumns";
import type { RawDataItem, StackOption } from "@/types/ChartData";
import type { TranslationFn } from "@/utils/registries";
import { sumBy } from "lodash-es";

/** Table filter columns the collapsed chart can break the rows down by. */
export type BreakdownColumn = Extract<
  SelectableFilterColumn,
  "technology_type" | "status" | "portfolio_type"
>;

export type BreakdownOption = StackOption<BreakdownColumn>;

export interface BreakdownSegment {
  /** The value as the stack option's `styleMap` keys it, so "null" for rows without one. */
  key: string;
  /** The raw field value the segment filters the table to; null for rows without one. */
  value: string | null;
  label: string;
  mwh: number;
  /** Share of the bar, 0–1. */
  share: number;
  color: string;
  /** Pale fill, so the inline label needs dark text. */
  isLight: boolean;
}

const DEFAULT_COLOR = "#ADB3AF";

/**
 * Sums the items' `yField` per value of the breakdown field, as bar segments in label order.
 * Rows without a value form their own segment, so the shares cover the same rows as the
 * panel's total; values that add up to no MWh are dropped.
 */
export const buildBreakdownSegments = (
  items: readonly RawDataItem[],
  breakdown: BreakdownOption,
  yField: string,
  t: TranslationFn
): BreakdownSegment[] => {
  const byKey = new Map<string, { value: string | null; mwh: number }>();
  for (const item of items) {
    const value: string | null = item[breakdown.fieldName];
    const key = String(value);
    const mwh = Number(item[yField]) || 0;
    byKey.set(key, { value, mwh: (byKey.get(key)?.mwh ?? 0) + mwh });
  }
  const total = sumBy([...byKey.values()], "mwh");
  return [...byKey.entries()]
    .filter(([, { mwh }]) => mwh > 0)
    .map(([key, { value, mwh }]) => {
      const color = breakdown.styleMap[key]?.backgroundColor ?? DEFAULT_COLOR;
      return {
        key,
        value,
        label: stackValueLabel(breakdown, key, t),
        mwh,
        share: mwh / total,
        color,
        isLight: isLightColor(color),
      };
    })
    .sort((a, b) => naturalCollator.compare(a.label, b.label));
};

// WCAG relative luminance of a #RRGGBB colour; anything unparseable is treated as light.
const isLightColor = (hex: string): boolean => {
  const match = /^#([0-9a-f]{6})$/i.exec(hex);
  if (!match) return true;
  const [r, g, b] = [0, 2, 4].map((offset) => {
    const channel = parseInt(match[1].slice(offset, offset + 2), 16) / 255;
    return channel <= 0.03928
      ? channel / 12.92
      : ((channel + 0.055) / 1.055) ** 2.4;
  });
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.3;
};
